import axios from "axios";
import { API_URL } from "../constants";

/**
 * The probation review (docs/prd/probation-salary-review.md §5, feature C):
 * the employee's direct manager (or HR, when there is none) rates 7 fixed
 * criteria and decides passed/failed before probation ends. Shapes follow
 * the API contract exactly — the backend is built in parallel against the
 * same document, no running instance to check against here.
 */

export const PROBATION_CRITERIA = [
  "quality",
  "workload",
  "independence",
  "discipline",
  "teamwork",
  "trainings",
  "targets",
] as const;
export type ProbationCriterionKey = (typeof PROBATION_CRITERIA)[number];

/** `na` is only ever valid for these two (§2). */
export const PROBATION_NA_CRITERIA: ProbationCriterionKey[] = ["trainings", "targets"];

export type ProbationRating = "meets" | "partly" | "not_meets" | "na";
export type ProbationReviewStatus = "pending" | "passed" | "failed" | "passed_unreviewed" | "cancelled";
export type ProbationReviewReason = "manager" | "no_manager" | "escalated";
export type ProbationDecision = "passed" | "failed";

export type DateValue = number | string | null;

export interface ProbationReviewPersonRef {
  id: string;
  name: string;
}

/** GET /:companyId/probation-reviews/mine — one row of `items`. */
export interface ProbationReviewListItem {
  userId: string;
  employeeName: string;
  positionLT: string;
  hiringDate: DateValue;
  probationEndDate: DateValue;
  decisionDeadline: DateValue;
  status: ProbationReviewStatus;
  reason: ProbationReviewReason;
}

/** GET/PUT/PATCH .../by-user/:userId — the `review` half of the answer. */
export interface ProbationReviewDetail {
  status: ProbationReviewStatus;
  probationEndDate: DateValue;
  defaultProbationEndDate: DateValue;
  requestAt: DateValue;
  decisionDeadline: DateValue;
  noticeDeadline: DateValue;
  employeeName: string;
  positionLT: string;
  hiringDate: DateValue;
  reviewer: ProbationReviewPersonRef | null;
  ratings: Partial<Record<ProbationCriterionKey, ProbationRating>> | null;
  comment: string | null;
  decidedBy: ProbationReviewPersonRef | null;
  decidedAt: number | null;
  absenceDaysHint: number | null;
  endDateEditedBy: ProbationReviewPersonRef | null;
  endDateEditedAt: number | null;
}

export interface ProbationReviewCan {
  submit: boolean;
  editEndDate: boolean;
}

export interface ProbationReviewByUserResponse {
  review: ProbationReviewDetail | null;
  can: ProbationReviewCan;
}

const str = (value: unknown): string => (value == null ? "" : String(value));
const strOrNull = (value: unknown): string | null => (value == null || value === "" ? null : String(value));
const dateOrNull = (value: unknown): DateValue =>
  typeof value === "number" || (typeof value === "string" && value !== "") ? value : null;
const numOrNull = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};
const arr = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const STATUSES: ProbationReviewStatus[] = ["pending", "passed", "failed", "passed_unreviewed", "cancelled"];
const statusOrPending = (value: unknown): ProbationReviewStatus =>
  STATUSES.includes(value as ProbationReviewStatus) ? (value as ProbationReviewStatus) : "pending";

const REASONS: ProbationReviewReason[] = ["manager", "no_manager", "escalated"];
const reasonOrManager = (value: unknown): ProbationReviewReason =>
  REASONS.includes(value as ProbationReviewReason) ? (value as ProbationReviewReason) : "manager";

function normalizeRef(raw: any): ProbationReviewPersonRef | null {
  if (!raw || typeof raw !== "object") return null;
  const id = str(raw.id);
  if (!id) return null;
  return { id, name: str(raw.name) };
}

/** One row of `mine`; exported for the mapping tests. */
export function normalizeProbationReviewListItem(raw: any): ProbationReviewListItem {
  return {
    userId: str(raw?.userId),
    employeeName: str(raw?.employeeName),
    positionLT: str(raw?.positionLT),
    hiringDate: dateOrNull(raw?.hiringDate),
    probationEndDate: dateOrNull(raw?.probationEndDate),
    decisionDeadline: dateOrNull(raw?.decisionDeadline),
    status: statusOrPending(raw?.status),
    reason: reasonOrManager(raw?.reason),
  };
}

/** The `review` half of a by-user answer; null stays null (nothing to normalize yet). */
export function normalizeProbationReviewDetail(raw: any): ProbationReviewDetail | null {
  if (!raw || typeof raw !== "object") return null;
  const ratings = raw.ratings && typeof raw.ratings === "object" ? raw.ratings : null;
  return {
    status: statusOrPending(raw.status),
    probationEndDate: dateOrNull(raw.probationEndDate),
    defaultProbationEndDate: dateOrNull(raw.defaultProbationEndDate),
    requestAt: dateOrNull(raw.requestAt),
    decisionDeadline: dateOrNull(raw.decisionDeadline),
    noticeDeadline: dateOrNull(raw.noticeDeadline),
    employeeName: str(raw.employeeName),
    positionLT: str(raw.positionLT),
    hiringDate: dateOrNull(raw.hiringDate),
    reviewer: normalizeRef(raw.reviewer),
    ratings,
    comment: strOrNull(raw.comment),
    decidedBy: normalizeRef(raw.decidedBy),
    decidedAt: numOrNull(raw.decidedAt),
    absenceDaysHint: numOrNull(raw.absenceDaysHint),
    endDateEditedBy: normalizeRef(raw.endDateEditedBy),
    endDateEditedAt: numOrNull(raw.endDateEditedAt),
  };
}

/** The full by-user answer; exported for the mapping tests. */
export function normalizeProbationReviewByUser(data: any): ProbationReviewByUserResponse {
  const raw = data || {};
  const can = raw.can || {};
  return {
    review: normalizeProbationReviewDetail(raw.review),
    can: { submit: can.submit === true, editEndDate: can.editEndDate === true },
  };
}

/** GET /:companyId/probation-reviews/mine (§5.1) — the queue page of the task list. */
export async function fetchProbationReviewsMine(
  companyId: string
): Promise<{ count: number; items: ProbationReviewListItem[] }> {
  const res = await axios.get(`${API_URL}/${companyId}/probation-reviews/mine`);
  const items = arr(res.data?.items).map(normalizeProbationReviewListItem);
  return { count: Number(res.data?.count) || items.length, items };
}

/** GET /:companyId/probation-reviews/by-user/:userId (§5.2). */
export async function fetchProbationReviewByUser(
  companyId: string,
  userId: string
): Promise<ProbationReviewByUserResponse> {
  const res = await axios.get(`${API_URL}/${companyId}/probation-reviews/by-user/${userId}`);
  return normalizeProbationReviewByUser(res.data);
}

/** PUT .../by-user/:userId/decision (§5.3). */
export async function submitProbationReviewDecision(
  companyId: string,
  userId: string,
  body: { decision: ProbationDecision; ratings: Partial<Record<ProbationCriterionKey, ProbationRating>>; comment?: string }
): Promise<ProbationReviewByUserResponse> {
  const res = await axios.put(`${API_URL}/${companyId}/probation-reviews/by-user/${userId}/decision`, body);
  return normalizeProbationReviewByUser(res.data);
}

/** PATCH .../by-user/:userId/end-date (§5.4, HR only). */
export async function updateProbationReviewEndDate(
  companyId: string,
  userId: string,
  probationEndDate: number
): Promise<ProbationReviewByUserResponse> {
  const res = await axios.patch(`${API_URL}/${companyId}/probation-reviews/by-user/${userId}/end-date`, {
    probationEndDate,
  });
  return normalizeProbationReviewByUser(res.data);
}
